import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/utils';
import {
  ToastContext,
  type AddToastInput,
  type ToastData,
  type ToastVariant,
} from './Toast';
import { TextButton } from './TextLink';

const DURATION_BY_VARIANT: Record<ToastVariant, number> = {
  error: 8_000,
  warning: 6_000,
  success: 3_000,
  info: 5_000,
};

const MAX_VISIBLE = 5;

/**
 * How long the exit animation runs before the toast is removed from state.
 * Must match the `duration-180` transition class on the toast element below -
 * removing state earlier cuts the animation off, later leaves it sitting still.
 */
const EXIT_ANIMATION_MS = 180;

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback((input: AddToastInput) => {
    const id = `toast-${++toastCounter}`;
    const duration = input.duration ?? DURATION_BY_VARIANT[input.variant];
    setToasts((prev) => [...prev.slice(-(MAX_VISIBLE - 1)), { ...input, id, duration }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext value={{ addToast }}>
      {children}
      {/* Portal-free: rendered at end of provider tree, positioned fixed */}
      <div
        aria-live="polite"
        aria-label="Notifications"
        className="fixed bottom-0 right-0 z-[9999] flex flex-col-reverse gap-2 p-4 sm:max-w-md w-full pointer-events-none"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
        ))}
      </div>
    </ToastContext>
  );
}

const VARIANT_STYLES: Record<ToastVariant, { accent: string; icon: string }> = {
  error: {
    accent: 'border-l-error',
    icon: 'text-error',
  },
  warning: {
    accent: 'border-l-warning',
    icon: 'text-warning',
  },
  success: {
    accent: 'border-l-success',
    icon: 'text-success',
  },
  info: {
    accent: 'border-l-secondary',
    icon: 'text-secondary',
  },
};

function ToastItem({ toast, onDismiss }: { toast: ToastData; onDismiss: (id: string) => void }) {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const dismiss = useCallback(() => {
    // Re-entrant by design: the close button, the action button and the auto-dismiss
    // timer can all fire for the same toast. Only the first schedules the removal -
    // a later one would overwrite exitTimerRef and orphan the timer it replaced.
    if (exitTimerRef.current !== undefined) return;
    setExiting(true);
    exitTimerRef.current = setTimeout(() => onDismiss(toast.id), EXIT_ANIMATION_MS);
  }, [onDismiss, toast.id]);

  useEffect(() => {
    if (toast.duration > 0) {
      timerRef.current = setTimeout(dismiss, toast.duration);
    }
    return () => clearTimeout(timerRef.current);
  }, [toast.duration, dismiss]);

  // Unmount only, and deliberately NOT folded into the effect above: that one's
  // cleanup runs whenever its deps change, which would cancel an exit animation
  // already in flight. This timer calls setState on the provider, so surviving
  // unmount means updating a torn-down tree - in a test run that lands after the
  // jsdom environment is gone, as `window is not defined`.
  useEffect(() => () => clearTimeout(exitTimerRef.current), []);

  const style = VARIANT_STYLES[toast.variant];

  return (
    <div
      role="alert"
      className={cn(
        'pointer-events-auto w-full rounded-lg border border-border border-l-4 bg-bg-primary shadow-lg',
        'transition-all duration-180',
        style.accent,
        exiting
          ? 'opacity-0 translate-x-4'
          : 'opacity-100 translate-x-0 animate-toast-in',
      )}
      onMouseEnter={() => clearTimeout(timerRef.current)}
      onMouseLeave={() => {
        if (toast.duration > 0) {
          timerRef.current = setTimeout(dismiss, toast.duration);
        }
      }}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <span className={cn('mt-0.5 shrink-0', style.icon)}>
          <ToastIcon variant={toast.variant} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary">{toast.title}</p>
          {toast.description && (
            <p className="mt-0.5 text-sm text-text-muted line-clamp-3">{toast.description}</p>
          )}
          {toast.action && (
            <TextButton
              onClick={() => {
                toast.action!.onClick();
                dismiss();
              }}
              className="mt-1.5 text-sm"
            >
              {toast.action.label}
            </TextButton>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 rounded p-0.5 text-text-light hover:text-text-muted transition-colors"
          aria-label="Dismiss"
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function ToastIcon({ variant }: { variant: ToastVariant }) {
  const cls = 'h-5 w-5';
  switch (variant) {
    case 'error':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
        </svg>
      );
    case 'warning':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      );
    case 'success':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
        </svg>
      );
    case 'info':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
        </svg>
      );
  }
}
